/*
 * Copyright (c) 2025.
 * This code and its derivatives belong to BETICA LTD and may not be copied, reproduced, amended or used in any way without the permission of BETICA LTD
 * @author torrejonv
 *
 */

/**
 * @file cmsSlice.js
 * @description Redux slice for managing cms related state and operations
 */

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import createAxiosInstance from "../async/axios";
import { getRequestParams } from "../async/get-fetch-params";

/**
 * @function getInitialState
 * @description Creates the initial state object for the cms slice
 * @param {Object} params - Initial state parameters
 * @returns {Object} The initial state object
 */
export const getInitialState = (config = null, originId = null) => ({
  config,
  error: null,
  errorCode: null,
  lineId: null,
  loading: false,
  originId,
});

/**
 * @function getCmsConfig
 * @description Async thunk for get cms config operations
 * @param {Object} data - Parameters for the operation
 * @returns {Promise<Object>} Result of the operation
 */
export const getCmsConfig = createAsyncThunk(
  "cms/loadConfig",
  async ({ cancelToken, lineId, originId, overriddenLanguage }, thunkAPI) => {
    try {
      const { authToken, language } = getRequestParams(thunkAPI.getState());
      const axios = createAxiosInstance(thunkAPI.dispatch, { authToken, language: overriddenLanguage || language });

      const result = await axios.get(`/player/cms/config?originId=${originId}&lineId=${lineId}`, {
        cancelToken,
      });

      return {
        config: result.data,
        lineId,
        originId,
      };
    } catch (err) {
      const customError = {
        code: err?.code,
        message: err.response?.headers["x-information"] || "Unable to obtain the cms config", // serializable (err.response.data)
        name: "CMS Config Error",
        status: err.response?.statusText,
      };
      throw customError;
    }
  },
);

/**
 * @constant cmsSlice
 * @description Redux slice for cms management
 */
const cmsSlice = createSlice({
  extraReducers: (builder) => {
    builder
      .addCase(getCmsConfig.pending, (state) => {
        state.error = null;
        state.errorCode = null;
        state.loading = true;
      })
      .addCase(getCmsConfig.rejected, (state, action) => {
        state.error = action.error.message;
        state.errorCode = action.error.code;
        state.loading = false;
      })
      .addCase(getCmsConfig.fulfilled, (state, action) => {
        state.config = action.payload.config;
        state.error = null;
        state.errorCode = null;
        state.lineId = action.payload.lineId;
        state.loading = false;
        state.originId = action.payload.originId;
      });
  },
  initialState: getInitialState(),
  name: "cms",
  reducers: {},
});
const { reducer } = cmsSlice;

export default reducer;
